import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import SongDTO from "../common/dto/SongDTO";
import SimpleButton from "../components/Buttons/SimpleButton";
import Column from "../components/Column/Column";
import { NavBar } from "../components/NavBar";
import Row from "../components/Row/Row";
import Text from "../components/Text/Text";
import { Timeline } from "../components/Timeline/Timeline";
import calendar_bg from "./imgs/calendar_bg.png";

interface SetLaunchPageProps {
  theme: string;
  toggleTheme: () => void;
}

const MAXIMUM_ALLOWED_YEARS = 5;

const SetLaunchPageContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  background: #000d30;
  background-size: cover;
  width: 99vw;
  height: 100vh;
  align-items: center;
  justify-content: center;
  position: relative;
`;
const Calendar = styled.div`
  border-radius: 10px;
  box-shadow: 0 6px 12px rgba(27, 37, 86, 0.16);
  overflow: hidden;
  filter: drop-shadow(2px 16px 19px rgba(0, 0, 0, 0.09));
  backdrop-filter: blur(80px);
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  & .react-datepicker__header {
    color: ${(props) => (props.theme.themeType === "dark" ? "white" : "black")};
    background: url(${(props) =>
        props.theme.themeType === "dark" ? calendar_bg : "white"})
      center;
    font-family: "Spartan";
  }
  & .react-datepicker__current-month {
    color: ${(props) => (props.theme.themeType === "dark" ? "white" : "black")};
  }

  & .react-datepicker-wrapper {
    float: right;
  }

  & .react-datepicker__day-name {
    color: ${(props) => (props.theme.themeType === "dark" ? "white" : "black")};
  }
  border-radius: 8px;
  & .react-datepicker__month {
    opacity: 1;
    margin: 0px;
    background: url(${(props) =>
        props.theme.themeType === "dark" ? calendar_bg : "white"})
      center;
    font-family: "Spartan";
  }

  & .react-datepicker__month-container {
    position: relative !important;
  }

  & .react-datepicker__day {
    color: ${(props) => (props.theme.themeType === "dark" ? "white" : "black")};
  }
  & .react-datepicker {
    position: relative !important;
    transform: inherit !important;
  }

  & .react-datepicker__day--selected {
    background: white;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-fill-color: transparent;
    border: none;
    -webkit-backdrop-filter: blur(2em);
    backdrop-filter: blur(2em);

    &::before {
      border-radius: 100%;
      position: absolute;
      background: #0f6c89;
      content: "";
      height: 25px;
      width: 25px;
      right: 0;
      opacity: 0.5;
    }
  }

  & .react-datepicker__day--today {
    border: none;
  }

  & .react-datepicker__day--keyboard-selected {
    background: none;
  }
`;

const NumberInput = styled("input")`
  margin: 5px;
  margin-top: 20px;
  margin-bottom: 20px;
  font-family: "Spartan";
  font-style: normal;
  font-weight: 500;
  font-size: 48px;
  line-height: 54px;
  border: none;

  background: white;
  &:focus {
    color: white;
    outline: none;
  }

  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
`;

interface DateProps {
  onDateChange: (date: Date) => void;
  fullDate: Date;
}

interface TimeProps {
  onDateChange: (date: Date) => void;
  fullDate: Date;
  ampm: number;
}
interface MonthProps {
  onMonthChange: (date: Date) => void;
  fullDate: Date;
}

interface YearProps {
  onYearChange: (date: Date) => void;
  fullDate: Date;
}

const NumberContainer = styled("div")`
  width: 50px;
  margin-right: 15px;
  border: 0.5px solid #a5efff60;
  filter: drop-shadow(2px 16px 19px rgba(0, 0, 0, 0.09));
  backdrop-filter: blur(40px);
  /* Note: backdrop-filter has minimal browser support */

  border-radius: 8px;
`;

const DateInput = React.forwardRef<HTMLDivElement, DateProps>(
  ({ onDateChange, fullDate }, ref) => {
    const monthsWith31Days = [1, 3, 5, 7, 8, 10, 12];
    const monthsWith28Days = [2];
    const monthsWith30Days = [4, 6, 9, 11];
    useEffect(() => {
      const value = fullDate.getDate();
      setFirstDigit(
        value
          ? value.toString().length === 1
            ? "0"
            : value.toString().at(0)
          : "0"
      );
      setSecondDigit(
        value
          ? value.toString().length === 1
            ? value.toString().at(0)
            : value.toString().at(1)
          : "0"
      );
    }, [fullDate]);

    const [firstDigit, setFirstDigit] = useState<string | undefined>("");
    const [secondDigit, setSecondDigit] = useState<string | undefined>("");

    const handleFirstDigitChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const num = event.target.value;
      if (/[^0-3]/.test(num)) return;
      setFirstDigit(num);
      // If the first digit is 3, change the digit to 0 since there can only be 30 and 31 days in a month
      if (num === "3" && parseInt(secondDigit as string) > 1) {
        onDigitChanged(num, "0");
        return;
      }
      // If the first digit is 0, change the digit to 1 since there is no zeroth day in the month.
      if (num === "0" && secondDigit === "0") {
        onDigitChanged(num, "1");
        return;
      }
      // Only if it isn't empty change
      if (num !== "") {
        onDigitChanged(num, secondDigit as string);
      }
    };
    const handleSecondDigitChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const num = event.target.value;
      if (/[^0-9]/.test(num)) return;
      if (monthsWith28Days.includes(fullDate.getMonth() + 1)) {
        if (firstDigit === "2" && parseInt(num) > 8) {
          return;
        }
      }
      if (monthsWith31Days.includes(fullDate.getMonth() + 1)) {
        if (firstDigit === "3" && parseInt(num) > 1) {
          return;
        }
      }

      if (monthsWith30Days.includes(fullDate.getMonth() + 1)) {
        if (firstDigit === "3" && parseInt(num) > 0) {
          return;
        }
      }

      if (firstDigit === "0" && num === "0") {
        return;
      }
      setSecondDigit(num);
      if (num !== "") {
        onDigitChanged(firstDigit as string, num);
      }
    };

    const onDigitChanged = (firstDigit: string, secondDigit: string) => {
      if (firstDigit === "0") {
        fullDate.setDate(parseInt(secondDigit));
      } else {
        fullDate.setDate(
          parseInt((firstDigit as string) + (secondDigit as string))
        );
      }
      onDateChange(fullDate);
    };

    return (
      <div ref={ref} style={{ display: "flex", flexDirection: "column" }}>
        <Row>
          <NumberContainer>
            {fullDate && (
              <NumberInput
                maxLength={1}
                onChange={handleFirstDigitChange}
                value={firstDigit}
                type="text"
              />
            )}
          </NumberContainer>

          <NumberContainer>
            {fullDate && (
              <NumberInput
                onChange={handleSecondDigitChange}
                value={secondDigit}
                maxLength={1}
                type="text"
              />
            )}
          </NumberContainer>
        </Row>

        <Text>Date</Text>
      </div>
    );
  }
);

const HoursInput = React.forwardRef<HTMLDivElement, TimeProps>(
  ({ onDateChange, fullDate, ampm }, ref) => {
    const [firstDigit, setFirstDigit] = useState<string | undefined>();
    const [secondDigit, setSecondDigit] = useState<string | undefined>();

    useEffect(() => {
      const value = fullDate.getHours() % 12 || 12;
      setFirstDigit(
        value
          ? value.toString().length === 1
            ? "0"
            : value.toString().at(0)
          : "0"
      );

      setSecondDigit(
        value
          ? value.toString().length === 1 || value.toString() === "0"
            ? value.toString().at(0)
            : value.toString().at(1)
          : "0"
      );
    }, [fullDate]);

    const handleFirstDigitChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const num = event.target.value;

      if (/[^0-1]/.test(num)) return;
      setFirstDigit(num);
      if (num !== "" && parseInt(secondDigit as string) > 2) {
        onDigitChange(num, "0");
        return;
      }
      if (num !== "") {
        onDigitChange(num, secondDigit as string);
      }
    };
    const handleSecondDigitChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const num = event.target.value;
      if (firstDigit === "1" && /[^0-2]/.test(num)) return;
      setSecondDigit(num);
      if (num !== "") {
        onDigitChange(firstDigit as string, num);
      }
    };

    const onDigitChange = (firstDigit: string, secondDigit: string) => {
      fullDate.setHours(
        ampm === 0
          ? parseInt((firstDigit as string) + (secondDigit as string))
          : parseInt((firstDigit as string) + (secondDigit as string)) + 12
      );
      onDateChange(fullDate);
    };

    return (
      <div ref={ref} style={{ display: "flex", flexDirection: "column" }}>
        <Row>
          <NumberContainer>
            {fullDate && (
              <NumberInput
                onChange={handleFirstDigitChange}
                value={firstDigit}
                maxLength={1}
                type="text"
              />
            )}
          </NumberContainer>

          <NumberContainer>
            <NumberInput
              onChange={handleSecondDigitChange}
              value={secondDigit}
              maxLength={1}
              type="text"
            />
          </NumberContainer>
        </Row>

        <Text>Hours</Text>
      </div>
    );
  }
);

const MinutesInput = React.forwardRef<HTMLDivElement, TimeProps>(
  ({ onDateChange, fullDate, ampm }, ref) => {
    const [firstDigit, setFirstDigit] = useState<string | undefined>();
    const [secondDigit, setSecondDigit] = useState<string | undefined>();

    useEffect(() => {
      const value = fullDate.getMinutes();

      setFirstDigit(
        value
          ? value.toString().length === 1
            ? "0"
            : value.toString().at(0)
          : "0"
      );

      setSecondDigit(
        value
          ? value.toString().length === 1
            ? value.toString().at(0)
            : value.toString().at(1)
          : "0"
      );
    }, [fullDate]);
    const handleFirstDigitChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const num = event.target.value;

      if (/[^0-5]/.test(num)) return;
      setFirstDigit(num);
      if (num !== "") {
        onDigitChange(num, secondDigit as string);
      }
    };
    const handleSecondDigitChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const num = event.target.value;

      if (/[^0-9]/.test(num)) return;
      setSecondDigit(num);
      if (num !== "") {
        onDigitChange(firstDigit as string, num);
      }
    };

    const onDigitChange = (firstDigit: string, secondDigit: string) => {
      fullDate.setMinutes(
        parseInt((firstDigit as string) + (secondDigit as string))
      );
      onDateChange(fullDate);
    };

    return (
      <div ref={ref} style={{ display: "flex", flexDirection: "column" }}>
        <Row>
          <NumberContainer>
            {fullDate && (
              <NumberInput
                onChange={handleFirstDigitChange}
                value={firstDigit}
                maxLength={1}
                type="text"
              />
            )}
            {!fullDate && (
              <NumberInput
                onChange={handleFirstDigitChange}
                value="0"
                type="text"
              />
            )}
          </NumberContainer>

          <NumberContainer>
            {fullDate && (
              <NumberInput
                onChange={handleSecondDigitChange}
                value={secondDigit}
                maxLength={1}
                type="text"
              />
            )}

            {!fullDate && (
              <NumberInput
                onChange={handleSecondDigitChange}
                maxLength={1}
                value="0"
                type="text"
              />
            )}
          </NumberContainer>
        </Row>

        <Text>Minutes</Text>
      </div>
    );
  }
);

const MonthInput = React.forwardRef<HTMLDivElement, MonthProps>(
  ({ onMonthChange, fullDate }, ref) => {
    const [firstDigit, setFirstDigit] = useState<string | undefined>("");
    const [secondDigit, setSecondDigit] = useState<string | undefined>("");

    useEffect(() => {
      const value = fullDate.getMonth() + 1;
      setFirstDigit(
        value
          ? value.toString().length === 1
            ? "0"
            : value.toString().at(0)
          : "0"
      );
      setSecondDigit(
        value
          ? value.toString().length === 1
            ? value.toString().at(0)
            : value.toString().at(1)
          : "0"
      );
    }, [fullDate]);
    const handleFirstDigitChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const num = event.target.value;
      if (/[^0-1]/.test(num)) return;
      setFirstDigit(num);
      if (num === "1" && parseInt(secondDigit as string) > 2) {
        onDigitChanged(num, "0");
        return;
      }
      if (num !== "") {
        onDigitChanged(num, secondDigit as string);
      }
    };
    const handleSecondDigitChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const num = event.target.value;
      if (/[^0-9]/.test(num)) return;
      setSecondDigit(num);
      if (num !== "") {
        onDigitChanged(firstDigit as string, num);
      }
    };

    const onDigitChanged = (firstDigit: string, secondDigit: string) => {
      if (firstDigit === "0") {
        fullDate.setMonth(parseInt(secondDigit) - 1);
      } else {
        fullDate.setMonth(
          parseInt((firstDigit as string) + (secondDigit as string)) - 1
        );
      }
      onMonthChange(fullDate);
    };
    return (
      <div ref={ref} style={{ display: "flex", flexDirection: "column" }}>
        <Row>
          <NumberContainer>
            {fullDate && (
              <NumberInput
                onChange={handleFirstDigitChange}
                value={firstDigit}
                maxLength={1}
                type="text"
              />
            )}
          </NumberContainer>

          <NumberContainer>
            {fullDate && (
              <NumberInput
                onChange={handleSecondDigitChange}
                value={secondDigit}
                maxLength={1}
                type="text"
              />
            )}
          </NumberContainer>
        </Row>
        <Text>Month</Text>
      </div>
    );
  }
);

const YearInput = React.forwardRef<HTMLDivElement, YearProps>(
  ({ onYearChange, fullDate }, ref) => {
    let value = fullDate.getFullYear();
    const [fourthDigit, setFourthDigit] = useState<string | undefined>("");
    useEffect(() => {
      setFourthDigit(fullDate.getFullYear().toString().at(3));
    }, [fullDate]);
    const handleFourthDigitChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const num = event.target.value;
      const today = new Date();

      if (/[^0-9]/.test(num)) return;
      const yearToSet = parseInt(
        today.getFullYear().toString().substring(0, 3) + num
      );
      // Only allow to a maximum of 5 years to be set into the future
      if (
        (num !== "" && yearToSet < today.getFullYear()) ||
        yearToSet > today.getFullYear() + MAXIMUM_ALLOWED_YEARS
      )
        return;
      setFourthDigit(num);
      if (num !== "") {
        onDigitChange(num);
      }
    };

    const onDigitChange = (newLastDigit: string) => {
      // example: getFullYear() = 2022,
      // fullDate.getFullYear() % 10 = 2 [the last digit of full year]
      // newLastDigit = 7;
      // 2022 - 2 = 2020
      // 2020 + 7 =  2027
      const newYear =
        fullDate.getFullYear() -
        (fullDate.getFullYear() % 10) +
        parseInt(newLastDigit);
      fullDate.setFullYear(newYear);
      onYearChange(fullDate);
    };

    return (
      <div ref={ref} style={{ display: "flex", flexDirection: "column" }}>
        <Row>
          <NumberContainer>
            {value && (
              <NumberInput
                readOnly={true}
                value={value ? value.toString().at(0) : "-1"}
                type="text"
              />
            )}
          </NumberContainer>

          <NumberContainer>
            {value && (
              <NumberInput
                readOnly={true}
                value={value ? value.toString().at(1) : "-1"}
                type="text"
              />
            )}
          </NumberContainer>
          <NumberContainer>
            {value && (
              <NumberInput
                readOnly={true}
                value={value ? value.toString().at(2) : "-1"}
                type="text"
              />
            )}
          </NumberContainer>
          <NumberContainer>
            {value && (
              <NumberInput
                onChange={handleFourthDigitChange}
                value={fourthDigit}
                type="text"
              />
            )}
          </NumberContainer>
        </Row>
        <Text>Year</Text>
      </div>
    );
  }
);

interface CustomDateInputProps {
  onDateChangeParent: (date: Date) => void;
  placeholder: string;
  value: Date;
  id: string;
}
const RowWithGap = styled(Row)`
  gap: 20px;
`;

const Select = styled.select`
  width: 100%;
  height: 35px;
  background: transparent;
  color: white;
  padding-left: 5px;
  font-size: 14px;

  border: 1.5px solid #a5efff;
  border-radius: 10px;

  option {
    color: white;
    background: black;
    display: flex;
    white-space: pre;
    min-height: 20px;
    padding: 0px 2px 1px;
  }
`;
const CustomDateInput = React.forwardRef<HTMLDivElement, CustomDateInputProps>(
  ({ onDateChangeParent, placeholder, value, id }, ref) => {
    const onDateChange = (date: Date) => {
      onDateChangeParent(date);
    };

    const [amPm, setAmPm] = useState(0);
    const onAmPmChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
      const newDate = new Date(value);
      if (event.target.value === "0") {
        newDate.setHours(newDate.getHours() % 12 || 12);
        onDateChange(newDate);
      } else {
        newDate.setHours(newDate.getHours() + 12);
        onDateChange(newDate);
      }
      setAmPm(parseInt(event.target.value));
    };

    return (
      <Column ref={ref} style={{ marginLeft: "100px", marginTop: "50px" }}>
        <RowWithGap>
          {value && (
            <DateInput onDateChange={onDateChange} fullDate={new Date(value)} />
          )}
          {value && (
            <MonthInput
              onMonthChange={onDateChange}
              fullDate={new Date(value)}
            />
          )}
          {value && (
            <YearInput onYearChange={onDateChange} fullDate={new Date(value)} />
          )}
        </RowWithGap>
        <RowWithGap>
          {value && (
            <HoursInput
              onDateChange={onDateChange}
              fullDate={new Date(value)}
              ampm={amPm}
            />
          )}
          {value && (
            <MinutesInput
              onDateChange={onDateChange}
              fullDate={new Date(value)}
              ampm={amPm}
            />
          )}
          {value && (
            <Select
              onChange={onAmPmChange}
              value={new Date(value).getHours() < 12 ? "0" : "1"}
              style={{ width: "15%" }}
            >
              <option value="0">AM</option>
              <option value="1">PM</option>
            </Select>
          )}
        </RowWithGap>
      </Column>
    );
  }
);
const NextButton = styled(SimpleButton)`
  width: 20%;
  align-self: end;
  margin-right: -300px;
  margin-top: 100px;
`;

interface SetLaunchPageState {
  songDto: SongDTO;
  timelineEvents: Array<string>;
  masterCopy: Boolean;
}

export const SetLaunchPage: React.FC<SetLaunchPageProps> = ({
  theme,
  toggleTheme,
}) => {
  const [startDate, setStartDate] = useState(new Date());
  const { state } = useLocation();
  const locationState = state as SetLaunchPageState;
  const navigate = useNavigate();

  const handleChange = (date: Date) => {
    setStartDate(date);
  };

  const onNextClick = () => {
    // Go to minting progress page
    const songDto = locationState.songDto;
    songDto.launchDate = startDate.getTime() / 1000;
    if (songDto.type === "0") {
      navigate("/minting_progress", {
        state: {
          songDto: songDto,
          timelineEvents: locationState.timelineEvents,
          listLater: false,
          listAtScheduledDate: true,
        },
      });
    } else {
      navigate("/collectible_deploy", {
        state: {
          songDto: songDto,
          timelineEvents: locationState.timelineEvents,
          listLater: true,
        },
      });
    }
  };

  return (
    <>
      <NavBar theme={theme} themeToggler={toggleTheme} hideNavLinks={true} />
      <SetLaunchPageContainer>
        <Timeline
          events={locationState.timelineEvents}
          currentEventIndex={3}
          header={
            locationState.masterCopy
              ? "Creating Master Copy NFT"
              : "Creating Collectible NFT"
          }
        />
        <Column>
          <Text style={{ marginLeft: "-230px" }}>
            Select Desired Launch Date Below
          </Text>
          <Wrapper>
            <DatePicker
              calendarContainer={Calendar}
              selected={startDate}
              open={true}
              onChange={handleChange}
              customInput={
                <CustomDateInput
                  onDateChangeParent={handleChange}
                  placeholder="hi"
                  value={startDate}
                  id="id"
                />
              }
              popperPlacement="left"
              popperModifiers={[
                {
                  name: "eventListeners",
                  options: {
                    scroll: false,
                  },
                },
                {
                  name: "offset",
                  options: {
                    offset: [0, 0],
                  },
                },
              ]}
              dateFormat="MM/dd/yyyy h:mm aa"
            />
          </Wrapper>
          <NextButton bg="primary" onClick={onNextClick}>
            Confirm
          </NextButton>
        </Column>
      </SetLaunchPageContainer>
    </>
  );
};
